/**
 * 메뉴 다중선택 도움창.
 *
 * 문서검색(SN_SYS_BAS_REP_001)의 메뉴명 조건에서 쓴다. 메뉴코드·메뉴명으로 걸러 고른
 * 메뉴를 여러 건 돌려준다.
 *
 * 다른 도움창(SinglePopup/MultiPopup)과 달리 서버를 부르지 않는다. 메뉴 목록은 로그인 직후
 * 전역 스토어(useMenuStore)에 이미 들어와 있어서(Header.tsx 와 같은 출처) 그걸 그대로 쓴다.
 * 그래서 sys_popupctrl·sys_popupgridcolumn·sys_query 등록이 필요 없다.
 *
 * 선택 결과는 CommonModal 규약대로 getSelectedRow() 로 넘긴다 (CommonModal.tsx:257).
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, TextField } from "@mui/material";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { useMenuStore } from "@shared/libs/Global";
import { StyleSettings } from "../../styles/StyleSettings";

interface MenuMultiPopupProps {
  /** cd_popup. 지금은 화면 동작에 쓰지 않고 컬럼 메타의 cd_menu 로만 들어간다 */
  code?: string;
  /** 호출한 컨트롤이 넘긴 초기 조건. str_search 가 있으면 메뉴명 검색어로 받는다 */
  selectedData?: any;
  /** 이미 선택돼 있던 행들 — 다시 열었을 때 체크를 살려 준다 */
  options?: any[];
  modalRef?: any;
}

/** 메뉴 목록에서 걸러낼 가상 부모 — 최근메뉴 / 북마크 (Header.tsx:47 과 같은 규칙) */
const EXCLUDED_PARENTS = ["SN_REC", "SN_FAV"];

/** 관리용 메뉴는 목록에 넣지 않는다 (Header.tsx:131) */
const EXCLUDED_FG_MENU = "MGM";

/**
 * 그리드 컬럼 — sys_formgridcolumn 한 행과 같은 모양으로 만든다.
 * SnColumnsCreate 가 이 snake_case 필드를 그대로 읽는다.
 */
const buildColumn = (
  cd_menu: string,
  cd_column: string,
  nm_column: string,
  no_seq: number,
  qt_width: number
) => ({
  cd_firm: "",
  cd_menu,
  cd_grid: "grid1",
  cd_user: "admin",
  cd_column,
  nm_column,
  tp_column: "SnTextBox",
  fg_visible: "Y",
  cd_displaycolumn: null,
  cd_clas: null,
  cd_popup: null,
  no_seq,
  qt_width,
  yn_required: "N",
  yn_allowsorting: "Y",
  yn_fixed: "N",
  tp_fixedposition: null,
  qt_groupindex: -1,
  yn_sum: "N",
  yn_system: "N",
  tp_format: null,
  // 조회 전용 — 셀을 고칠 수 없게 잠근다
  dc_editcon: "1 == 1",
  yn_drilldown: "N",
  dc_filter: null,
});

const MenuMultiPopup = forwardRef(
  (
    { code = "", selectedData, options, modalRef }: MenuMultiPopupProps,
    ref
  ) => {
    const { menuItems } = useMenuStore();
    const gridApiRef = useRef<any>(null);

    const [cdMenu, setCdMenu] = useState("");
    const [nmMenu, setNmMenu] = useState(
      String(selectedData?.str_search ?? "")
    );

    /** 조회 버튼을 눌렀을 때의 조건 — 타이핑만으로 목록이 흔들리지 않게 분리해 둔다 */
    const [cond, setCond] = useState({
      cd_menu: "",
      nm_menu: String(selectedData?.str_search ?? ""),
    });

    /**
     * 지금까지 고른 메뉴 — 메뉴코드로 담아 둔다.
     *
     * 체크 상태를 그리드에만 맡기면 조회 조건을 바꿀 때 목록이 새로 그려지면서
     * (SnGrid.tsx:422 가 fixCheckbox 를 false 로 다시 깐다) 앞서 고른 것이 사라진다.
     * 그래서 선택은 여기 모아 두고, 목록이 바뀌면 이 값으로 체크를 되돌린다.
     * 조건을 여러 번 바꿔 가며 계속 더할 수 있어야 해서 필요한 장치다.
     */
    const pickedRef = useRef<Map<string, any>>(new Map());
    const [pickedCount, setPickedCount] = useState(0);

    const columns = useMemo(
      () => [
        buildColumn(code, "cd_menu", "메뉴코드", 1, 200),
        buildColumn(code, "nm_menu", "메뉴명", 2, 260),
      ],
      [code]
    );

    /**
     * 실제로 고를 수 있는 메뉴.
     *
     * 스토어에는 최근메뉴·북마크가 같은 메뉴를 다시 담고 있어서 그대로 쓰면 같은 메뉴가
     * 여러 번 나온다. 가상 부모를 걸러낸 뒤 메뉴코드로 한 번 더 중복을 제거한다.
     */
    const allRows = useMemo(() => {
      const seen = new Set<string>();
      return (menuItems ?? [])
        .filter(
          (item: any) =>
            item?.cd_menu &&
            item.fg_menu !== EXCLUDED_FG_MENU &&
            !EXCLUDED_PARENTS.includes(item.cd_menu_parent)
        )
        .filter((item: any) => {
          const key = String(item.cd_menu);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((item: any) => ({
          cd_menu: String(item.cd_menu ?? ""),
          nm_menu: String(item.nm_menu ?? ""),
        }))
        .sort((a, b) => a.nm_menu.localeCompare(b.nm_menu, "ko"));
    }, [menuItems]);

    const rows = useMemo(() => {
      const cd = cond.cd_menu.trim().toLowerCase();
      const nm = cond.nm_menu.trim().toLowerCase();
      if (!cd && !nm) return allRows;
      return allRows.filter(
        (row) =>
          (!cd || row.cd_menu.toLowerCase().includes(cd)) &&
          (!nm || row.nm_menu.toLowerCase().includes(nm))
      );
    }, [allRows, cond]);

    // 열 때 넘겨받은 지난 선택을 담아 둔다
    useEffect(() => {
      const map = new Map<string, any>();
      (options ?? []).forEach((row: any) => {
        const key = String(row?.cd_menu ?? "");
        if (key) map.set(key, { cd_menu: key, nm_menu: row?.nm_menu ?? "" });
      });
      pickedRef.current = map;
      setPickedCount(map.size);
    }, [options]);

    /**
     * 화면에 뿌려진 행의 체크 상태를 pickedRef 로 옮긴다.
     * 지금 목록에 없는 선택은 그대로 남겨 둬야 조건을 바꿔도 누적이 유지된다.
     */
    const syncPicked = useCallback(() => {
      const api = gridApiRef.current;
      if (!api) return;
      const map = pickedRef.current;
      api.forEachNode?.((node: any) => {
        const key = String(node?.data?.cd_menu ?? "");
        if (!key) return;
        if (node.data.fixCheckbox) map.set(key, node.data);
        else map.delete(key);
      });
      setPickedCount(map.size);
    }, []);

    /**
     * 목록이 새로 그려진 뒤 지난 선택을 체크로 되돌린다.
     *
     * 행 데이터에 fixCheckbox 를 미리 넣어도 SnGrid 가 false 로 덮으므로
     * 그려진 다음에 노드를 찾아 체크해야 한다.
     */
    useEffect(() => {
      if (pickedRef.current.size === 0) return;
      const api = gridApiRef.current;
      if (!api) return;

      const apply = (attempt = 0) => {
        if ((api.getDisplayedRowCount?.() ?? 0) === 0) {
          if (attempt < 30) requestAnimationFrame(() => apply(attempt + 1));
          return;
        }
        api.forEachNode?.((node: any) => {
          const key = String(node?.data?.cd_menu ?? "");
          if (key && pickedRef.current.has(key) && !node.data.fixCheckbox) {
            api.setCheckedRow?.(node, true);
          }
        });
      };
      apply();
    }, [rows]);

    /**
     * 확인을 누르면 CommonModal 이 이걸 불러 결과로 쓴다.
     * 체크한 것이 없으면 포커스된 한 건이라도 돌려준다 — 체크 없이 확인만 누른 경우.
     */
    const getSelectedRow = () => {
      syncPicked();
      if (pickedRef.current.size > 0) {
        return Array.from(pickedRef.current.values());
      }
      const api = gridApiRef.current;
      return api?.getSelectedRows?.() ?? [];
    };

    const onSearch = useCallback(() => {
      // 조건을 바꾸기 전에 지금 체크 상태를 걷어 둔다 (목록이 바뀌면 체크가 초기화된다)
      syncPicked();
      setCond({ cd_menu: cdMenu, nm_menu: nmMenu });
    }, [cdMenu, nmMenu, syncPicked]);

    useImperativeHandle(ref, () => ({
      getSelectedRow,
      onSearch,
    }));

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSearch();
      }
    };

    // 더블클릭은 그 한 건을 더해서 바로 닫는다
    const onRowDoubleClicked = (e: any) => {
      if (!e?.data?.cd_menu) return;
      syncPicked();
      pickedRef.current.set(String(e.data.cd_menu), e.data);
      modalRef?.current?.handleConfirm(
        true,
        Array.from(pickedRef.current.values())
      );
    };

    const onCheckedRowChaged = () => syncPicked();

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* 검색조건 — 서버 메타(sys_popupctrl)를 쓰지 않으므로 여기서 직접 그린다 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: StyleSettings.font, whiteSpace: "nowrap" }}>
            메뉴코드
          </span>
          <TextField
            value={cdMenu}
            onChange={(e) => setCdMenu(e.target.value)}
            onKeyDown={onKeyDown}
            size="small"
            sx={{
              width: 180,
              "& .MuiInputBase-root": { height: StyleSettings.control_height },
              "& .MuiInputBase-input": { fontSize: StyleSettings.font },
            }}
          />
          <span style={{ fontSize: StyleSettings.font, whiteSpace: "nowrap" }}>
            메뉴명
          </span>
          <TextField
            value={nmMenu}
            onChange={(e) => setNmMenu(e.target.value)}
            onKeyDown={onKeyDown}
            size="small"
            sx={{
              width: 220,
              "& .MuiInputBase-root": { height: StyleSettings.control_height },
              "& .MuiInputBase-input": { fontSize: StyleSettings.font },
            }}
          />
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={onSearch}
            sx={{ width: 80 }}
          >
            조회
          </Button>
          <span
            style={{
              marginLeft: "auto",
              fontSize: StyleSettings.font,
              color: "#666",
              whiteSpace: "nowrap",
            }}
          >
            {rows.length}건 / 선택 {pickedCount}건
          </span>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <SnGrid
            ref={gridApiRef}
            columnConfig={columns}
            rowData={rows}
            etcProps={{
              // 이걸 안 주면 SnGrid 기본값이 singleRow 라 한 건만 잡힌다 (SnGrid.tsx:117)
              rowSelection: "multiple",
              isEditable: false,
              ischeckboxSelection: true,
              suppressInitialCellFocus: true,
            }}
            events={{
              onRowDoubleClicked,
              onCheckedRowChaged,
            }}
          />
        </div>
      </div>
    );
  }
);

export default MenuMultiPopup;
